import React, { useEffect, useMemo, useState } from "react";
import {
  MapPin,
  Instagram,
  Star,
  Sparkles,
  Clock,
  ChevronRight,
} from "lucide-react";
import ProductReveal from "@/components/ProductReveal";
import { useScrollDirection } from "@/hooks/useScrollDirection";

const FILE_BASE_URL = "https://statik.megriva.com/files/";
const CAMPAIGN_CATEGORY_ID = "campaigns-category";

const buildImageUrl = (path) => {
  if (!path || typeof path !== "string") return "";

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  return `${FILE_BASE_URL}${path.replace(/^\/+/, "")}`;
};

const normalizeIds = (ids = []) =>
  Array.isArray(ids)
    ? ids.filter((id) => id !== null && id !== undefined).map(String)
    : [];

const getId = (item = {}) =>
  item.id ?? item.Id ?? item.productId ?? item.ProductId ?? null;

const getCategoryIds = (item = {}) => {
  if (Array.isArray(item.categoryIds)) return normalizeIds(item.categoryIds);
  if (Array.isArray(item.CategoryIds)) return normalizeIds(item.CategoryIds);

  if (Array.isArray(item.categories)) {
    return normalizeIds(
      item.categories.map((c) => c?.id ?? c?.Id ?? c?.categoryId ?? c)
    );
  }

  if (Array.isArray(item.Categories)) {
    return normalizeIds(
      item.Categories.map((c) => c?.id ?? c?.Id ?? c?.categoryId ?? c)
    );
  }

  const single =
    item.category_id ??
    item.categoryId ??
    item.CategoryId ??
    item.category?.id ??
    item.Category?.id ??
    item.Category?.Id ??
    null;

  return single ? [String(single)] : [];
};

const getPromotionIds = (item = {}) => {
  if (Array.isArray(item.promotionIds)) return normalizeIds(item.promotionIds);
  if (Array.isArray(item.PromotionIds)) return normalizeIds(item.PromotionIds);

  if (Array.isArray(item.promotions)) {
    return normalizeIds(
      item.promotions.map((p) => p?.id ?? p?.Id ?? p?.promotionId ?? p)
    );
  }

  if (Array.isArray(item.Promotions)) {
    return normalizeIds(
      item.Promotions.map((p) => p?.id ?? p?.Id ?? p?.promotionId ?? p)
    );
  }

  return [];
};

const normalizeCategory = (category = {}) => ({
  ...category,
  id: category.id ?? category.Id ?? category.categoryId ?? category.CategoryId,
  name:
    category.name ??
    category.Name ??
    category.categoryName ??
    category.CategoryName ??
    "Kategori",
});

const normalizeMenuItem = (item = {}, fallbackCategoryId = null) => {
  const id = getId(item);
  const categoryIds = getCategoryIds(item);

  const finalCategoryIds =
    categoryIds.length > 0
      ? categoryIds
      : fallbackCategoryId
      ? [String(fallbackCategoryId)]
      : [];

  const firstCategoryId = finalCategoryIds[0] ?? null;

  return {
    ...item,
    id,
    name: item.name ?? item.Name ?? item.productName ?? item.ProductName ?? "",
    description:
      item.description ??
      item.Description ??
      item.productDescription ??
      item.ProductDescription ??
      "",
    price: item.price ?? item.Price ?? item.productPrice ?? item.ProductPrice,
    discountedRate:
      item.discountedRate ??
      item.DiscountedRate ??
      item.discounted_rate ??
      item.discountRate ??
      item.discount_rate ??
      0,
    categoryIds: finalCategoryIds,
    category_id: firstCategoryId,
    categoryId: firstCategoryId,
    CategoryId: firstCategoryId,
    promotionIds: getPromotionIds(item),
    image_url:
      item.image_url ??
      item.imageUrl ??
      item.ImageUrl ??
      item.image ??
      item.Image ??
      item.fileUrl ??
      item.FileUrl ??
      item.imageFile?.fileUrl ??
      item.ImageFile?.fileUrl ??
      item.ImageFile?.FileUrl ??
      "",
  };
};

const getDiscountRate = (item = {}) => {
  const value = Number(item.discountedRate ?? item.DiscountedRate ?? 0);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

const getDiscountedPrice = (item = {}) => {
  const price = Number(item.price || 0);
  const rate = getDiscountRate(item);

  if (!price || !rate) return null;

  return price - price * (rate / 100);
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === "") return "";

  const value = Number(price);
  if (Number.isNaN(value)) return "";

  return `${value.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ₺`;
};

const PriceBadge = ({ item, align = "left" }) => {
  const discountedPrice = getDiscountedPrice(item);
  const hasDiscount = getDiscountRate(item) > 0 && discountedPrice !== null;

  return (
    <div className="inline-flex flex-col rounded-xl border border-zinc-200 bg-zinc-100 px-2.5 py-1.5 whitespace-nowrap">
      {hasDiscount ? (
        <div className={align === "right" ? "text-right" : "text-left"}>
          <span className="block text-[11px] font-bold leading-none text-zinc-400 line-through sm:text-xs">
            {formatPrice(item.price)}
          </span>

          <span className="mt-1 block text-sm font-black leading-none text-emerald-600 sm:text-base">
            {formatPrice(discountedPrice)}
          </span>
        </div>
      ) : (
        <span className="text-sm font-black leading-none text-zinc-900 sm:text-base">
          {formatPrice(item.price)}
        </span>
      )}
    </div>
  );
};

const DiscountBadge = ({ item }) => {
  const rate = getDiscountRate(item);

  if (!rate) return null;

  return (
    <span className="inline-flex items-center rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-black text-white shadow-sm">
      %{rate} İndirim
    </span>
  );
};

const isBannerCurrentlyVisible = (banner = {}) => {
  const active = banner.is_active ?? banner.isActive ?? banner.IsActive ?? true;

  if (!active) return false;

  const now = new Date();
  const startDate = banner.startDate ?? banner.StartDate;
  const endDate = banner.endDate ?? banner.EndDate;

  if (startDate) {
    const start = new Date(startDate);

    if (!Number.isNaN(start.getTime()) && now < start) {
      return false;
    }
  }

  if (endDate) {
    const end = new Date(endDate);

    if (!Number.isNaN(end.getTime()) && now > end) {
      return false;
    }
  }

  return true;
};

const isFeaturedItem = (item = {}) =>
  Boolean(
    item.is_featured ||
      item.isFeatured ||
      item.IsFeatured ||
      item.featured ||
      item.popular ||
      item.is_popular
  );

const getItemImage = (item = {}) =>
  buildImageUrl(item.image_url || item.imageUrl || item.ImageUrl || "");

const getCampaignProductsText = (items = []) => {
  if (!items.length) return "Bu kampanya için içerik bilgisi bulunmuyor.";

  return items.map((item) => item.name).join(", ");
};

export default function PreviewMenuView({
  categories = [],
  menuItems = [],
  products = [],
  banners = [],
  businessInfo = {},
  customerPopupData = null,
  loading = false,
  loadingText = "Yükleniyor...",
  emptyText = "Henüz eklenmiş kategori veya içerik bulunmuyor.",
  audience = "admin",
}) {
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [businessPopupOpen, setBusinessPopupOpen] = useState(false);

  const scrollDirection = useScrollDirection();

  const normalizedCategories = useMemo(() => {
    return Array.isArray(categories)
      ? categories.map(normalizeCategory).filter((category) => category.id)
      : [];
  }, [categories]);

  const normalizedMenuItems = useMemo(() => {
    const directItems =
      Array.isArray(menuItems) && menuItems.length
        ? menuItems
        : Array.isArray(products)
        ? products
        : [];

    const itemsFromCategories = normalizedCategories.flatMap((category) => {
      const originalCategory = categories.find(
        (item) => String(normalizeCategory(item).id) === String(category.id)
      );

      const categoryProducts =
        originalCategory?.products ??
        originalCategory?.Products ??
        originalCategory?.menuItems ??
        originalCategory?.MenuItems ??
        originalCategory?.items ??
        originalCategory?.Items ??
        [];

      return Array.isArray(categoryProducts)
        ? categoryProducts.map((item) => normalizeMenuItem(item, category.id))
        : [];
    });

    const mergedItems = [
      ...directItems.map((item) => normalizeMenuItem(item)),
      ...itemsFromCategories,
    ];

    const map = new Map();

    mergedItems.forEach((item) => {
      if (!item?.id) return;

      map.set(String(item.id), item);
    });

    return Array.from(map.values());
  }, [menuItems, products, categories, normalizedCategories]);

  const visibleBanners = useMemo(() => {
    return [...banners]
      .filter(isBannerCurrentlyVisible)
      .sort(
        (a, b) =>
          Number(a.order ?? a.Order ?? 0) - Number(b.order ?? b.Order ?? 0)
      );
  }, [banners]);

  const campaignEntries = useMemo(() => {
    return visibleBanners
      .map((banner) => {
        const bannerId = banner.id ?? banner.Id;

        const productIds = normalizeIds(
          banner.selectedProductIds ??
            banner.SelectedProductIds ??
            banner.productIds ??
            banner.ProductIds ??
            []
        );

        let items = normalizedMenuItems.filter((item) =>
          productIds.includes(String(item.id))
        );

        if (!items.length) {
          items = normalizedMenuItems.filter((item) =>
            getPromotionIds(item).includes(String(bannerId))
          );
        }

        return {
          id: `campaign-entry-${bannerId}`,
          realId: bannerId,
          title: banner.title ?? banner.Title ?? "Kampanya",
          description: banner.description ?? banner.Description ?? "",
          image_url: banner.image_url ?? banner.imageUrl ?? banner.ImageUrl ?? "",
          items,
          productsText: getCampaignProductsText(items),
        };
      })
      .filter((campaign) => campaign.items.length > 0);
  }, [visibleBanners, normalizedMenuItems]);

  const visibleCategories = useMemo(() => {
    return normalizedCategories.map((category) => ({
      category,
      items: normalizedMenuItems.filter((item) =>
        getCategoryIds(item).includes(String(category.id))
      ),
    }));
  }, [normalizedCategories, normalizedMenuItems]);

  const orderedVisibleCategories = useMemo(() => {
    const campaignRootCategory =
      campaignEntries.length > 0
        ? [
            {
              category: {
                id: CAMPAIGN_CATEGORY_ID,
                name: "Kampanyalar",
              },
              items: campaignEntries,
            },
          ]
        : [];

    return [...campaignRootCategory, ...visibleCategories];
  }, [campaignEntries, visibleCategories]);

  const hasAnyItem = orderedVisibleCategories.length > 0;

  const headerTitle =
    businessInfo?.name ||
    businessInfo?.Name ||
    businessInfo?.businessName ||
    businessInfo?.BusinessName ||
    businessInfo?.business?.name ||
    businessInfo?.business?.Name ||
    businessInfo?.Business?.Name ||
    businessInfo?.subDomain ||
    businessInfo?.SubDomain ||
    "İşletme";

  const headerLogo =
    businessInfo.logoUrl &&
    (businessInfo.logoUrl.startsWith("http://") ||
    businessInfo.logoUrl.startsWith("https://")
      ? businessInfo.logoUrl
      : buildImageUrl(businessInfo.logoUrl));

  const businessAddress =
    businessInfo?.address ||
    businessInfo?.Address ||
    businessInfo?.businessAddress ||
    businessInfo?.BusinessAddress ||
    businessInfo?.location ||
    businessInfo?.Location ||
    businessInfo?.business?.address ||
    businessInfo?.business?.Address ||
    businessInfo?.Business?.address ||
    businessInfo?.Business?.Address ||
    "Adres bilgisi belirtilmemiş";

  const businessWorkingHours =
    businessInfo.workingHours ||
    businessInfo.WorkingHours ||
    businessInfo.openingHours ||
    businessInfo.OpeningHours ||
    "Çalışma saatleri belirtilmemiş";

  const isBusinessOpen =
    businessInfo.isOpen ?? businessInfo.IsOpen ?? businessInfo.open ?? null;

  const businessRating = businessInfo.rating || businessInfo.Rating || "Puan bilgisi yok";

  const hasPopupValue = (value) =>
    typeof value === "string" && value.trim().length > 0;

  const hasBusinessPopupContent =
    hasPopupValue(customerPopupData?.title) ||
    hasPopupValue(customerPopupData?.description) ||
    hasPopupValue(customerPopupData?.instagramUrl) ||
    hasPopupValue(customerPopupData?.reviewUrl) ||
    hasPopupValue(customerPopupData?.mapsUrl);

  const hasBusinessPopup = hasBusinessPopupContent;

  const popupReviewUrl =
    customerPopupData?.reviewUrl ??
    customerPopupData?.ReviewUrl ??
    customerPopupData?.review_url ??
    "#";

  const popupInstagramUrl =
    customerPopupData?.instagramUrl ??
    customerPopupData?.InstagramUrl ??
    customerPopupData?.instagram_url ??
    "#";

  const popupMapsUrl =
    customerPopupData?.mapsUrl ??
    customerPopupData?.MapsUrl ??
    customerPopupData?.maps_url ??
    "#";

  const selectedItemImage = selectedItem ? getItemImage(selectedItem) : "";

  const selectedItemCategoryName = selectedItem
    ? normalizedCategories.find((category) =>
        getCategoryIds(selectedItem).includes(String(category.id))
      )?.name || "İçerik"
    : "";

  const selectedCampaignImage = selectedCampaign
    ? buildImageUrl(selectedCampaign.image_url)
    : "";

  useEffect(() => {
    if (activeBannerIndex > visibleBanners.length - 1) {
      setActiveBannerIndex(0);
    }
  }, [visibleBanners, activeBannerIndex]);

  useEffect(() => {
    if (visibleBanners.length <= 1) return;

    const interval = setInterval(() => {
      setActiveBannerIndex((prev) =>
        prev === visibleBanners.length - 1 ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [visibleBanners]);

  useEffect(() => {
    const isAnyOverlayOpen =
      selectedItem || selectedCampaign || businessPopupOpen;

    if (!isAnyOverlayOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedItem(null);
        setSelectedCampaign(null);
        setBusinessPopupOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [selectedItem, selectedCampaign, businessPopupOpen]);

  useEffect(() => {
    if (audience !== "public" || !hasBusinessPopupContent) return;

    const sessionKey = `customer-popup-shown:${
      businessInfo.subDomain || businessInfo.name || "business"
    }`;

    if (sessionStorage.getItem(sessionKey) === "1") return;

    const timer = setTimeout(() => {
      setBusinessPopupOpen(true);
      sessionStorage.setItem(sessionKey, "1");
    }, 900);

    return () => clearTimeout(timer);
  }, [
    audience,
    hasBusinessPopupContent,
    businessInfo.subDomain,
    businessInfo.name,
  ]);

  const handleTabClick = (categoryId) => (event) => {
    event.preventDefault();
    setActiveCategoryId(categoryId);

    window.scrollTo({
      top: 260,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (activeCategoryId || orderedVisibleCategories.length === 0) return;

    setActiveCategoryId(orderedVisibleCategories[0].category.id);
  }, [activeCategoryId, orderedVisibleCategories]);

  const displayedCategories = orderedVisibleCategories.filter(
    ({ category }) => String(category.id) === String(activeCategoryId)
  );

  return (
    <div className="mx-auto min-h-screen w-full max-w-6xl overflow-x-hidden bg-[#FCFBF9] px-4 py-5 md:px-8 md:py-8">
      <div className="relative mb-6 overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white shadow-[0_22px_65px_rgba(24,24,27,0.06)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.18),_transparent_38%),radial-gradient(circle_at_bottom_right,_rgba(24,24,27,0.08),_transparent_34%)]" />

        <div className="relative p-5 md:p-7">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-[24px] border border-zinc-200 bg-white shadow-sm md:h-20 md:w-20">
              {headerLogo ? (
                <img
                  src={headerLogo}
                  alt={headerTitle}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-zinc-100 text-2xl font-bold text-zinc-700">
                  {headerTitle.charAt(0)}
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 pt-8 md:pt-0">
              <h1
                className="truncate text-2xl font-extrabold tracking-[-0.035em] text-zinc-950 md:text-4xl"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                {headerTitle}
              </h1>

              <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500">
                <MapPin className="h-4 w-4 text-zinc-500" />
                {businessAddress}
              </span>

              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs font-semibold text-zinc-500">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-zinc-500" />
                  {businessWorkingHours}
                </span>

                <span className="inline-flex items-center gap-1.5">
                  <Star className="h-4 w-4 fill-orange-400 text-orange-400" />
                  {businessRating}
                </span>
              </div>

              <div className="mt-3 flex gap-2">
                <span className="flex h-8 items-center justify-center whitespace-nowrap rounded-full border border-zinc-200 bg-white px-3 text-center text-xs font-semibold tracking-tight text-zinc-700">
                  {visibleCategories.length} kategori
                </span>

                <span className="flex h-8 items-center justify-center whitespace-nowrap rounded-full border border-zinc-200 bg-white px-3 text-center text-xs font-semibold tracking-tight text-zinc-700">
                  {normalizedMenuItems.length} içerik
                </span>

                {campaignEntries.length > 0 && (
                  <span className="flex h-8 items-center justify-center whitespace-nowrap rounded-full border border-zinc-200 bg-white px-3 text-center text-xs font-semibold tracking-tight text-zinc-700">
                    {campaignEntries.length} kampanya
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="absolute right-4 top-4">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                typeof isBusinessOpen !== "boolean"
                  ? "bg-zinc-100 text-zinc-600"
                  : isBusinessOpen
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {typeof isBusinessOpen === "boolean"
                ? isBusinessOpen
                  ? "Açık"
                  : "Kapalı"
                : "Durum belirtilmemiş"}
            </span>
          </div>
        </div>
      </div>

      {!loading && visibleBanners.length > 0 && (
        <div className="mb-6">
          <div className="relative overflow-hidden rounded-[22px] border border-zinc-200/80 bg-white shadow-sm">
            {visibleBanners.map((banner, index) => {
              const bannerImage = buildImageUrl(
                banner.image_url || banner.imageUrl || banner.ImageUrl || ""
              );

              return (
                <div
                  key={banner.id ?? banner.Id ?? index}
                  className={index === activeBannerIndex ? "block" : "hidden"}
                >
                  <div className="relative h-[200px] w-full overflow-hidden bg-zinc-100 md:h-[240px]">
                    {bannerImage ? (
                      <img
                        src={bannerImage}
                        alt={banner.title || banner.Title || "Kampanya"}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-800 to-orange-900">
                        <div className="rounded-2xl border border-white/15 bg-white/10 px-5 py-4 text-center text-white shadow-lg backdrop-blur">
                          <div className="text-3xl">🎁</div>
                          <div className="mt-1 text-xs font-black uppercase tracking-wide">
                            Özel Kampanya
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                    <div className="absolute inset-x-0 bottom-0 px-6 pb-5 text-white md:px-10">
                      <h3 className="text-lg font-bold md:text-xl">
                        {banner.title || banner.Title || "Kampanya"}
                      </h3>

                      {(banner.description || banner.Description) && (
                        <p className="mt-1 line-clamp-2 max-w-2xl text-sm text-white/90">
                          {banner.description || banner.Description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {visibleBanners.length > 1 && (
            <div className="mt-3 flex items-center justify-center gap-1.5">
              {visibleBanners.map((banner, index) => (
                <button
                  key={banner.id ?? banner.Id ?? index}
                  type="button"
                  onClick={() => setActiveBannerIndex(index)}
                  className={`rounded-full transition-all ${
                    index === activeBannerIndex
                      ? "h-1.5 w-5 bg-zinc-800"
                      : "h-1.5 w-1.5 bg-zinc-300"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {hasBusinessPopup &&
        !businessPopupOpen &&
        !selectedItem &&
        !selectedCampaign && (
          <button
            type="button"
            onClick={() => setBusinessPopupOpen(true)}
            className="fixed right-4 z-[9999] inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/95 px-4 py-3 text-sm font-semibold text-zinc-900 shadow-[0_10px_30px_rgba(15,23,42,0.16)] backdrop-blur hover:bg-white md:right-6"
            style={{
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
            }}
          >
            <Sparkles className="h-4 w-4 text-orange-500" />
            {audience === "public" ? "İşletme Bilgileri" : "Popup Önizleme"}
          </button>
        )}

      {!loading && hasAnyItem && (
        <div className="sticky top-0 z-20 -mx-4 border-y border-zinc-100 bg-white/75 px-4 py-3 backdrop-blur-xl md:-mx-8 md:px-8">
          <div className="flex w-full gap-2 overflow-x-auto overflow-y-hidden whitespace-nowrap no-scrollbar">
            {orderedVisibleCategories.map(({ category }) => {
              const isActive = activeCategoryId === category.id;
              const isCampaignRoot = category.id === CAMPAIGN_CATEGORY_ID;

              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={handleTabClick(category.id)}
                  className={[
                    "shrink-0 rounded-full px-4 py-2 text-sm font-semibold tracking-tight transition-all duration-300 active:scale-[0.97]",
                    isActive
                      ? "bg-[#EA580C] text-white shadow-[0_8px_22px_rgba(234,88,12,0.20)]"
                      : isCampaignRoot
                      ? "bg-orange-50 text-orange-700 hover:bg-orange-100"
                      : "bg-zinc-100/70 text-zinc-600 hover:bg-zinc-200/70 hover:text-zinc-900",
                  ].join(" ")}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="h-3" />

      {loading && (
        <div className="mt-6">
          <div className="mb-4 text-center text-sm text-zinc-600">
            {loadingText}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-[24px] border border-zinc-200/80 bg-white shadow-sm"
              >
                <div className="aspect-[4/3] w-full animate-pulse bg-zinc-200" />

                <div className="space-y-3 p-3">
                  <div className="h-4 w-3/4 animate-pulse rounded-full bg-zinc-200" />
                  <div className="h-3 w-full animate-pulse rounded-full bg-zinc-100" />
                  <div className="h-3 w-2/3 animate-pulse rounded-full bg-zinc-100" />

                  <div className="flex items-center justify-between pt-2">
                    <div className="h-8 w-20 animate-pulse rounded-xl bg-zinc-200" />
                    <div className="h-8 w-16 animate-pulse rounded-full bg-zinc-100" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && !hasAnyItem && (
        <div className="mt-16 text-center text-zinc-500">{emptyText}</div>
      )}

      {!loading &&
        displayedCategories.map(({ category, items }) => {
          const isCampaignRoot = category.id === CAMPAIGN_CATEGORY_ID;

          return (
            <section key={category.id} className="mb-8 w-full">
              <div className="mb-3 flex items-center justify-between gap-3 border-b border-zinc-200 pb-2">
                <div className="flex min-w-0 items-center gap-2">
                  <h2 className="truncate text-lg font-extrabold tracking-[-0.02em] text-zinc-950 md:text-xl">
                    {category.name}
                  </h2>

                  {isCampaignRoot && (
                    <span className="shrink-0 rounded-full bg-zinc-900 px-2.5 py-1 text-[10px] font-semibold text-white">
                      Kampanya
                    </span>
                  )}
                </div>

                <span className="shrink-0 rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs text-zinc-500">
                  {items.length} {isCampaignRoot ? "kampanya" : "içerik"}
                </span>
              </div>

              {isCampaignRoot ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {items.map((campaign) => {
                    const campaignImage = buildImageUrl(campaign.image_url);

                    return (
                      <div
                        key={campaign.id}
                        className="group relative overflow-hidden rounded-[26px] border border-zinc-200/80 bg-white shadow-[0_16px_45px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:border-zinc-300 hover:shadow-[0_24px_65px_rgba(15,23,42,0.14)]"
                      >
                        <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          <div className="absolute -inset-1 rounded-[26px] bg-gradient-to-r from-orange-400/20 via-pink-400/20 to-purple-400/20 blur-xl" />
                        </div>

                        <div className="relative h-[190px] w-full overflow-hidden bg-gradient-to-br from-zinc-100 via-zinc-50 to-zinc-200">
                          {campaignImage ? (
                            <img
                              src={campaignImage}
                              alt={campaign.title}
                              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-orange-50 via-white to-zinc-100">
                              <div className="rounded-2xl border border-orange-100 bg-white/85 px-5 py-4 text-center shadow-sm">
                                <div className="text-2xl">🎁</div>
                                <div className="mt-1 text-[10px] font-black uppercase tracking-wide text-orange-500">
                                  Kampanya Görseli
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="absolute left-3 top-3">
                            <span className="rounded-full border border-white/60 bg-white/90 px-2.5 py-1 text-[10px] font-bold text-zinc-900 shadow-sm backdrop-blur">
                              Kampanya
                            </span>
                          </div>
                        </div>

                        <div className="absolute right-3 top-3">
                          <span className="rounded-full bg-zinc-950 px-2.5 py-1 text-[10px] font-bold text-white shadow-lg">
                            ÖNE ÇIKAN
                          </span>
                        </div>

                        <div className="flex min-h-[220px] flex-col p-4">
                          <div className="flex-1">
                            <h3 className="line-clamp-2 text-base font-extrabold text-zinc-900">
                              {campaign.title}
                            </h3>

                            <p className="mt-2 line-clamp-2 text-sm leading-6 text-zinc-600">
                              {campaign.description ||
                                "Kampanya açıklaması bulunmuyor."}
                            </p>

                            <div className="mt-3 rounded-2xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-3 shadow-inner">
                              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                                Kampanya İçeriği
                              </p>

                              <p className="line-clamp-4 text-sm leading-6 text-zinc-800">
                                {campaign.productsText}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 flex items-center justify-between gap-2 border-t border-zinc-100 pt-3">
                            <span className="rounded-xl border border-zinc-200 bg-zinc-100 px-2.5 py-1.5 text-xs font-semibold text-zinc-900">
                              {campaign.items.length} içerik dahil
                            </span>

                            <button
                              type="button"
                              onClick={() => setSelectedCampaign(campaign)}
                              className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-900 transition hover:border-zinc-900 hover:bg-zinc-900 hover:text-white"
                            >
                              İncele
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {items.map((item, index) => {
                    const imageSrc = getItemImage(item);
                    const featured = isFeaturedItem(item);

                    return (
                      <ProductReveal
                        key={`${category.id}-${item.id}`}
                        direction={scrollDirection}
                        index={index}
                      >
                        <div className="group relative flex gap-4 overflow-hidden rounded-[26px] border border-zinc-200/80 bg-white p-3 shadow-[0_14px_35px_rgba(15,23,42,0.06)] transition-all duration-300 hover:border-zinc-300 hover:shadow-[0_22px_55px_rgba(15,23,42,0.12)]">
                          <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <div className="absolute -inset-1 rounded-[24px] bg-gradient-to-r from-orange-400/10 via-pink-400/10 to-purple-400/10 blur-xl" />
                          </div>

                          <div className="relative h-32 w-32 shrink-0 overflow-hidden rounded-[22px] bg-gradient-to-br from-zinc-100 via-white to-zinc-200">
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={item.name}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                                loading="lazy"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-100 via-white to-zinc-200">
                                <div className="rounded-2xl border border-zinc-200 bg-white/80 px-4 py-3 text-center shadow-sm">
                                  <div className="text-xl">◈</div>
                                  <div className="mt-1 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
                                    Görsel Yok
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
                              {featured && (
                                <span className="rounded-full border border-white/60 bg-white/90 px-2.5 py-1 text-[10px] font-bold text-zinc-900 shadow-sm backdrop-blur">
                                  Öne Çıkan
                                </span>
                              )}

                              <DiscountBadge item={item} />
                            </div>
                          </div>

                          <div className="flex min-h-[128px] flex-1 flex-col py-1">
                            <div className="flex-1">
                              <p className="line-clamp-2 text-[15px] font-black leading-snug text-zinc-950">
                                {item.name}
                              </p>

                              <p className="mt-1.5 line-clamp-2 min-h-[38px] text-xs leading-5 text-zinc-500">
                                {item.description ||
                                  "İçerik açıklaması bulunmuyor."}
                              </p>
                            </div>

                            <div className="mt-3 flex items-center justify-between">
                              <div className="flex flex-col">
                                {getDiscountRate(item) > 0 && (
                                  <span className="text-xs text-zinc-400 line-through">
                                    {formatPrice(item.price)}
                                  </span>
                                )}

                                <span className="text-lg font-extrabold leading-none text-[#EA580C]">
                                  {formatPrice(
                                    getDiscountedPrice(item) ?? item.price
                                  )}
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={() => setSelectedItem(item)}
                                className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-600 transition hover:text-zinc-900"
                              >
                                İncele
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </ProductReveal>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}

      {selectedItem && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/55 backdrop-blur-[2px] md:items-center"
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="w-full max-h-[90dvh] overflow-y-auto rounded-t-3xl bg-white shadow-2xl md:max-w-md md:rounded-3xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative">
              {selectedItemImage ? (
                <img
                  src={selectedItemImage}
                  alt={selectedItem.name}
                  className="aspect-[4/3] w-full object-cover"
                />
              ) : (
                <div className="aspect-[4/3] w-full bg-zinc-100" />
              )}
            </div>

            <div className="p-4 md:p-5">
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-full border border-zinc-200 bg-zinc-100 px-2.5 py-1 text-[10px] font-semibold text-zinc-700">
                  {selectedItemCategoryName}
                </span>

                <DiscountBadge item={selectedItem} />
              </div>

              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-extrabold text-zinc-900 md:text-xl">
                  {selectedItem.name}
                </h3>

                <PriceBadge item={selectedItem} align="right" />
              </div>

              <p className="mt-4 text-sm leading-6 text-zinc-600">
                {selectedItem.description ||
                  "Bu içerik için açıklama bulunmuyor."}
              </p>

              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="mt-5 w-full rounded-full bg-[#EA580C] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#C2410C]"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedCampaign && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/55 backdrop-blur-[2px] md:items-center"
          onClick={() => setSelectedCampaign(null)}
        >
          <div
            className="w-full max-h-[90dvh] overflow-y-auto rounded-t-3xl bg-white shadow-2xl md:max-w-lg md:rounded-3xl"
            onClick={(event) => event.stopPropagation()}
          >
            {selectedCampaignImage ? (
              <img
                src={selectedCampaignImage}
                alt={selectedCampaign.title}
                className="h-[220px] w-full object-cover"
              />
            ) : (
              <div className="h-[220px] w-full bg-zinc-100" />
            )}

            <div className="p-4 md:p-5">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="rounded-full bg-zinc-900 px-2.5 py-1 text-[10px] font-semibold text-white">
                  Kampanya
                </span>
              </div>

              <h3 className="text-lg font-extrabold text-zinc-900 md:text-xl">
                {selectedCampaign.title}
              </h3>

              <p className="mt-3 text-sm leading-6 text-zinc-600">
                {selectedCampaign.description ||
                  "Bu kampanya için açıklama bulunmuyor."}
              </p>

              <div className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                <p className="mb-1 text-sm font-semibold text-zinc-900">
                  Kampanya İçeriği
                </p>

                <p className="text-sm leading-6 text-zinc-600">
                  {selectedCampaign.productsText}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedCampaign(null)}
                className="mt-5 w-full rounded-xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {businessPopupOpen && hasBusinessPopup && (
        <div
          className="fixed inset-0 z-[110] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/60 backdrop-blur-[3px] md:items-center md:p-4"
          onClick={() => setBusinessPopupOpen(false)}
        >
          <div
            className="w-full max-h-[90dvh] overflow-y-auto rounded-t-[28px] border border-white/30 bg-white shadow-2xl md:max-w-xl md:rounded-[32px]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="bg-zinc-950 px-5 py-6 text-white md:px-7 md:py-8">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]">
                İşletme Bilgileri
              </div>

              <h3 className="mt-4 text-2xl font-bold tracking-tight md:text-3xl">
                {customerPopupData?.title?.trim() ||
                  "Bizi değerlendirin ve bizi takip edin"}
              </h3>

              <p className="mt-3 text-sm leading-6 text-white/80 md:text-base">
                {customerPopupData?.description?.trim() ||
                  "Deneyiminizi paylaşabilir, Instagram hesabımıza ulaşabilir ve konumdan bize kolayca gelebilirsiniz."}
              </p>
            </div>

            <div className="grid gap-3 p-5 md:grid-cols-3 md:p-6">
              <a
                href={popupReviewUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => {
                  if (popupReviewUrl === "#") event.preventDefault();
                }}
                className={`rounded-2xl border border-zinc-200 bg-zinc-50 p-4 transition hover:bg-white ${
                  popupReviewUrl === "#" ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Star className="h-5 w-5 text-orange-500" />
                <div className="mt-3 text-sm font-semibold text-zinc-900">
                  İşletmeyi Değerlendir
                </div>
              </a>

              <a
                href={popupInstagramUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => {
                  if (popupInstagramUrl === "#") event.preventDefault();
                }}
                className={`rounded-2xl border border-zinc-200 bg-zinc-50 p-4 transition hover:bg-white ${
                  popupInstagramUrl === "#"
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <Instagram className="h-5 w-5 text-pink-500" />
                <div className="mt-3 text-sm font-semibold text-zinc-900">
                  Instagram
                </div>
              </a>

              <a
                href={popupMapsUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => {
                  if (popupMapsUrl === "#") event.preventDefault();
                }}
                className={`rounded-2xl border border-zinc-200 bg-zinc-50 p-4 transition hover:bg-white ${
                  popupMapsUrl === "#" ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <MapPin className="h-5 w-5 text-emerald-600" />
                <div className="mt-3 text-sm font-semibold text-zinc-900">
                  Konum Bilgisi
                </div>
              </a>
            </div>

            <div className="border-t border-zinc-100 px-5 py-4 md:px-6">
              <button
                type="button"
                onClick={() => setBusinessPopupOpen(false)}
                className="w-full rounded-xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800"
              >
                Vitrine Dön
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-12 border-t border-zinc-200 pb-10 pt-6 text-center">
        <div className="text-sm text-zinc-600">
          © {new Date().getFullYear()} {headerTitle}
        </div>

        <div className="mt-2 text-xs text-zinc-400">
          Powered by{" "}
          <a
            href="https://www.megriva.com"
            target="_blank"
            rel="noreferrer"
            className="font-semibold transition hover:text-zinc-700"
          >
            megriva 🚀
          </a>
        </div>
      </div>
    </div>
  );
}